/**
 * ProfileCompletion component
 * Modal/screen for existing users who need to provide first and last names.
 * Blocks access to the main application until profile is complete.
 */

import * as React from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { api } from '@/services/api';
import type { UserProfile, UpdateProfileRequest } from '@/types';

export interface ProfileCompletionProps {
  /** Current user email for display */
  email: string;
  /** Callback when profile is successfully completed */
  onComplete: (profile: UserProfile) => void;
}

export function ProfileCompletion({ email, onComplete }: ProfileCompletionProps) {
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Form validation errors
  const [firstNameError, setFirstNameError] = React.useState('');
  const [lastNameError, setLastNameError] = React.useState('');

  const validateFirstName = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setFirstNameError('First name is required');
      return false;
    }
    if (trimmed.length > 100) {
      setFirstNameError('First name cannot exceed 100 characters');
      return false;
    }
    setFirstNameError('');
    return true;
  };

  const validateLastName = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setLastNameError('Last name is required');
      return false;
    }
    if (trimmed.length > 100) {
      setLastNameError('Last name cannot exceed 100 characters');
      return false;
    }
    setLastNameError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate all fields
    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);

    if (!isFirstNameValid || !isLastNameValid) {
      return;
    }

    setIsLoading(true);

    try {
      const updateData: UpdateProfileRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };

      const updatedProfile = await api.put<UserProfile, UpdateProfileRequest>(
        '/users/me',
        updateData
      );

      onComplete(updatedProfile);
    } catch (err) {
      console.error('Profile update error:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-canvas">
      <div className="w-full max-w-md px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-black leading-tight tracking-tight text-primary">
            Complete your profile
          </h1>
          <p className="mt-3 text-md text-secondary leading-relaxed">
            Please provide your name to continue using AdVerdict
          </p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-border-subtle bg-surface p-8 shadow-md">
          {/* User Email Display */}
          <div className="mb-6 text-center">
            <p className="text-sm text-muted">Signed in as</p>
            <p className="mt-1 font-medium text-primary">{email}</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div
              className="mb-6 rounded-md border border-danger/20 bg-danger-soft px-4 py-3"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                id="firstName"
                type="text"
                label="First name"
                placeholder="Jane"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (firstNameError) validateFirstName(e.target.value);
                }}
                onBlur={() => validateFirstName(firstName)}
                error={!!firstNameError}
                errorMessage={firstNameError}
                disabled={isLoading}
                autoComplete="given-name"
                autoFocus
                required
              />
              <Input
                id="lastName"
                type="text"
                label="Last name"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (lastNameError) validateLastName(e.target.value);
                }}
                onBlur={() => validateLastName(lastName)}
                error={!!lastNameError}
                errorMessage={lastNameError}
                disabled={isLoading}
                autoComplete="family-name"
                required
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isLoading}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Saving...' : 'Continue'}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-muted">
          Your name will be visible to other team members
        </p>
      </div>
    </div>
  );
}
